import { CloseIcon, CommentsIcon } from '~components/icons'
import { Box, Button, Fab, Grow, Typography } from '@mui/material'
import { useState } from 'react'
import { ExactScopeName, useHasScope } from '~hooks/permissions/use-has-scope'
import { COLORS } from '~theme/colors'
import { typography } from '~theme/global'
import { DialogBox } from '~pages/pages-behind-login/chartering/pre-fixture-page/components/dialog-box'
import useBreakpoint from '~hooks/use-breakpoint'

export const teamsChannels = [
  {
    area: 'General',
    link: 'msteams://teams.microsoft.com/l/channel/19%3AfP69f__K6f3edstPV9ZKUAXsu1fhtUh76dP1_5neh_Q1%40thread.tacv2/General?groupId=abcd8e2a-dfd4-4c92-ba7f-fd4947897512&tenantId=b55d03e2-fd95-480a-91ca-9cd58f42f07c',
  },
  {
    area: 'Chartering',
    link: 'msteams://teams.microsoft.com/l/channel/19%3A28528eb0b2aa4043a3f5a27a4b2cb9dd%40thread.tacv2/Chartering?groupId=abcd8e2a-dfd4-4c92-ba7f-fd4947897512&tenantId=b55d03e2-fd95-480a-91ca-9cd58f42f07c',
  },
  {
    area: 'Operations',
    link: 'msteams://teams.microsoft.com/l/channel/19%3A764382b03e8744a2807923f4df85127a%40thread.tacv2/Operations?groupId=abcd8e2a-dfd4-4c92-ba7f-fd4947897512&tenantId=b55d03e2-fd95-480a-91ca-9cd58f42f07c',
  },
  {
    area: 'Pool Management',
    link: 'msteams://teams.microsoft.com/l/channel/19%3A3a9e5ed290c84a8d898b4b8556e3fd7b%40thread.tacv2/Pool%20Excellence?groupId=abcd8e2a-dfd4-4c92-ba7f-fd4947897512&tenantId=b55d03e2-fd95-480a-91ca-9cd58f42f07c',
  },
]

export function ReportIssue() {
  const isMtUser = useHasScope(ExactScopeName.maerskTankersUser)

  return isMtUser ? <ReportIssueInternal /> : <ReportIssueExternal />
}

function ReportIssueExternal() {
  const [isFabOpen, setIsFabOpen] = useState(false)

  return (
    <FabWrapper isFabOpen={isFabOpen} setIsFabOpen={setIsFabOpen}>
      <FabActionButton
        component="a"
        href="mailto:digital-support@maersktankers.com"
        onClick={() => setIsFabOpen(false)}
      />
    </FabWrapper>
  )
}

function ReportIssueInternal() {
  const [isFabOpen, setIsFabOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const onClose = () => {
    setIsModalOpen(false)
    setIsFabOpen(false)
  }

  return (
    <>
      <FabWrapper isFabOpen={isFabOpen} setIsFabOpen={setIsFabOpen}>
        <FabActionButton
          onClick={() => {
            setIsModalOpen(true)
            setIsFabOpen(false)
          }}
        />
      </FabWrapper>

      {isModalOpen && (
        <DialogBox open onClose={onClose}>
          <Typography variant="h2" sx={{ mb: 4 }}>
            Which business area is affected?
          </Typography>
          <Box>
            <Typography variant="paragraph2">
              By clicking the area you will be directed to a specific Teams
              Channel
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 5 }}>
              {teamsChannels.map((teamsChannel) => (
                <Box
                  component="a"
                  key={teamsChannel.area}
                  href={teamsChannel.link}
                  onClick={onClose}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textDecoration: 'none',
                    p: 3,
                    borderRadius: '100px',
                    border: `solid 1px ${COLORS.primary.maerskBlue}`,
                    boxShadow: '0px 4px 25px 0px rgba(0, 0, 0, 0.08)',
                    ...typography.paragraph2,
                    textTransform: 'uppercase',
                    '&:hover': {
                      backgroundColor: COLORS.primary.maerskBlue,
                      color: COLORS.primary.white,
                    },
                  }}
                >
                  {teamsChannel.area}
                </Box>
              ))}
            </Box>
          </Box>
        </DialogBox>
      )}
    </>
  )
}

function FabWrapper({
  children,
  isFabOpen,
  setIsFabOpen,
}: {
  children: React.ReactNode
  isFabOpen: boolean
  setIsFabOpen: (open: boolean) => void
}) {
  const { isMobileSize } = useBreakpoint(true)

  return (
    <Box
      sx={{
        bottom: isMobileSize ? 52 : 96,
        right: 16,
        position: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        alignItems: 'flex-end',
        zIndex: 9998,
      }}
    >
      {isFabOpen && (
        <Grow in={isFabOpen} style={{ transformOrigin: '0 0 0' }}>
          <Box>{children}</Box>
        </Grow>
      )}
      <Fab
        onClick={() => setIsFabOpen(!isFabOpen)}
        aria-label="report issue"
        sx={{
          backgroundColor: COLORS.secondary.deepBlue,
          borderRadius: isMobileSize ? '8px' : isFabOpen ? '50px' : '16px',
          transition: '0.3s cubic-bezier(0.22, 1, 0.36, 1)',
          width: isMobileSize ? '36px' : isFabOpen ? '48px' : '56px',
          height: isMobileSize ? '36px' : isFabOpen ? '48px' : '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          border: 'none',
          ...typography.menu,
          color: COLORS.primary.white,
          '&:hover': {
            backgroundColor: COLORS.secondary.midBlue,
          },
        }}
      >
        {isFabOpen ? (
          <CloseIcon fontSize="20px" />
        ) : (
          <CommentsIcon fontSize="20px" />
        )}
      </Fab>
    </Box>
  )
}

function FabActionButton({
  component = 'button',
  ...props
}: {
  onClick: () => void
  component?: 'a' | 'button'
  href?: string
}) {
  return (
    <Button
      component={component}
      {...props}
      sx={{
        backgroundColor: COLORS.secondary.lightBlue,
        ...typography.paragraph1,
        textTransform: 'none',
        px: 4,
        py: 3,
        borderRadius: '28px',
      }}
    >
      Report an issue
    </Button>
  )
}
